import React, { useState } from 'react';
import { ClipboardList, ChevronDown, ChevronUp, X } from 'lucide-react';
import { useTrading, useMarketData } from '../context/MarketContext';
import type { Order, Trade } from '../types';
import { TIF_LABELS, TIF_OPTIONS } from '../types/orders';
import { colors } from '../styles/tokens';
import ProgressBar from '../components/ProgressBar';
import { Panel } from '../components/ui';
import FillHistoryTable from '../components/FillHistoryTable';
import { INSTRUMENTS } from '../constants/instruments';

const STATUS_COLORS: Record<string, string> = {
  new: colors.accent.blue,
  filled: colors.accent.green,
  partially_filled: colors.accent.yellow,
  rejected: colors.accent.red,
  cancelled: colors.text.secondary,
};

function timeShort(iso: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleTimeString('en-TZ', { timeZone: 'Africa/Nairobi', hour12: false });
  } catch { return iso; }
}

function symbolToSecId(symbol: string): string {
  const upper = (symbol || '').toUpperCase();
  if (INSTRUMENTS.some(i => i.secId.toUpperCase() === upper)) return upper;
  const byLabel = INSTRUMENTS.find(i => i.label.toUpperCase() === upper);
  return byLabel ? byLabel.secId.toUpperCase() : upper;
}

function fmtVal(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toFixed(0);
}

function slippageColor(side: string, bps: number): string {
  return (side === 'buy' ? bps > 0 : bps < 0) ? colors.accent.yellow : colors.accent.green;
}

type TabKey = 'all' | 'active' | 'filled' | 'rejected' | 'cancelled';

export default function OrdersPage() {
  const { orders, cancelOrder, updateOrderTif } = useTrading();
  const { selectedSymbol } = useMarketData();
  const [activeTab, setActiveTab] = useState<TabKey>('all');
  const [filterSymbol, setFilterSymbol] = useState<string>(selectedSymbol || 'all');
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [dateFilter, setDateFilter] = useState<string>(''); // 'YYYY-MM-DD', '' = no filter

  const toggleExpand = (id: string) => {
    setExpandedOrders(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Date filter — narrows to orders on a specific EAT date (current session only)
  // TODO: Historical orders from past sessions require GET /api/orders?symbol&start&end endpoint
  const dateFilteredOrders = dateFilter
    ? orders.filter(o => {
        if (!o.time) return false;
        // Swedish locale produces 'YYYY-MM-DD' format in the given timezone
        return new Date(o.time).toLocaleDateString('sv', { timeZone: 'Africa/Nairobi' }) === dateFilter;
      })
    : orders;

  // Stats (from date-filtered set)
  const activeCount   = dateFilteredOrders.filter(o => o.status === 'new' || o.status === 'partially_filled').length;
  const partialCount  = dateFilteredOrders.filter(o => o.status === 'partially_filled').length;
  const filledCount   = dateFilteredOrders.filter(o => o.status === 'filled').length;
  const rejectedCount = dateFilteredOrders.filter(o => o.status === 'rejected').length;
  const cancelledCount = dateFilteredOrders.filter(o => o.status === 'cancelled').length;

  // Fill stats — computed from date-filtered orders for the selected symbol only
  const fillStatsOrders = filterSymbol === 'all'
    ? dateFilteredOrders
    : dateFilteredOrders.filter(o => symbolToSecId(o.symbol) === filterSymbol.toUpperCase());

  const fillStats = (() => {
    let buyFilledQty = 0, buyTotalQty = 0, buyFilledValue = 0, buyTotalValue = 0;
    let sellFilledQty = 0, sellTotalQty = 0, sellFilledValue = 0, sellTotalValue = 0;
    let buyWeightedSum = 0, sellWeightedSum = 0;
    for (const o of fillStatsOrders) {
      const filled = o.cumQty ?? 0;
      const total = (o.cumQty ?? 0) + (o.leavesQty ?? 0);
      const avgFill = o.avgFillPrice ?? o.price ?? 0;
      if (o.side === 'buy') {
        buyFilledQty += filled;
        buyTotalQty += total;
        buyFilledValue += filled * avgFill;
        buyTotalValue += total * (o.price ?? 0);
        buyWeightedSum += filled * avgFill;
      } else {
        sellFilledQty += filled;
        sellTotalQty += total;
        sellFilledValue += filled * avgFill;
        sellTotalValue += total * (o.price ?? 0);
        sellWeightedSum += filled * avgFill;
      }
    }
    return {
      buyFilledQty, buyTotalQty, buyFilledValue, buyTotalValue,
      buyAvgFill: buyFilledQty > 0 ? buyWeightedSum / buyFilledQty : 0,
      sellFilledQty, sellTotalQty, sellFilledValue, sellTotalValue,
      sellAvgFill: sellFilledQty > 0 ? sellWeightedSum / sellFilledQty : 0,
    };
  })();

  // Filter by tab
  const byTab = dateFilteredOrders.filter(o => {
    if (activeTab === 'active') return o.status === 'new' || o.status === 'partially_filled';
    if (activeTab === 'filled') return o.status === 'filled';
    if (activeTab === 'rejected') return o.status === 'rejected';
    if (activeTab === 'cancelled') return o.status === 'cancelled';
    return true;
  });

  // Filter by symbol
  const filteredOrders = byTab.filter(o =>
    filterSymbol === 'all' || symbolToSecId(o.symbol) === filterSymbol.toUpperCase()
  );

  const recentOwnFills: Trade[] = (() => {
    const fills: Trade[] = [];
    for (const o of orders) {
      if (filterSymbol !== 'all' && symbolToSecId(o.symbol) !== filterSymbol.toUpperCase()) continue;
      for (const f of o.fills ?? []) {
        const fx: any = f;
        fills.push({
          time: f.time,
          price: Number(f.price ?? 0),
          size: Number(f.quantity ?? 0),
          side: o.side,
          symbol: o.symbol,
          is_maker: fx.is_maker,
          realized_pnl: fx.realized_pnl,
          microprice_at_fill: fx.microprice_at_fill,
        });
      }
    }
    return fills
      .filter(t => t.price > 0 && t.size > 0)
      .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
      .slice(0, 100);
  })();

  const card = { background: colors.bg.secondary, borderRadius: 8, border: `1px solid ${colors.border.default}` };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <ClipboardList size={16} color="#7e8a96" />
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: colors.text.primary }}>Order Blotter</h2>
      </div>

      {/* Zone 1 — Stats Strip */}
      <div style={{ display: 'flex', gap: 10 }}>
        <StatChip label="Total" count={dateFilteredOrders.length} />
        <StatChip label="Active" count={activeCount} color="#4da3ff" />
        <StatChip label="Partial" count={partialCount} color="#ff8c3a" />
        <StatChip label="Filled" count={filledCount} color="#36c98e" />
        <StatChip label="Rejected" count={rejectedCount} color="#ff5c5c" />
        <StatChip label="Cancelled" count={cancelledCount} color="#7e8a96" />
      </div>

      {/* Zone 1b — Fill Summary (filled/total qty, avg fill price, total value) */}
      {(fillStats.buyTotalQty > 0 || fillStats.sellTotalQty > 0) && (
        <Panel title="Fill summary" badge={filterSymbol !== 'all' ? filterSymbol : 'ALL SYMBOLS'}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {/* BUY side */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: colors.accent.green, letterSpacing: '0.06em' }}>BUY SIDE</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: colors.text.secondary }}>
                <span>Filled / Total Qty</span>
                <span style={{ fontWeight: 700, color: colors.text.primary, fontVariantNumeric: 'tabular-nums' }}>
                  {fillStats.buyFilledQty.toLocaleString()} / {fillStats.buyTotalQty.toLocaleString()}
                  <span style={{ color: colors.text.muted, fontWeight: 400 }}>{' '}({fillStats.buyTotalQty > 0 ? ((fillStats.buyFilledQty / fillStats.buyTotalQty) * 100).toFixed(0) : 0}%)</span>
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: colors.text.secondary }}>
                <span>Avg Fill Price</span>
                <span style={{ fontWeight: 700, color: colors.accent.green, fontVariantNumeric: 'tabular-nums' }}>
                  {fillStats.buyAvgFill > 0 ? fillStats.buyAvgFill.toFixed(2) : '—'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: colors.text.secondary }}>
                <span>Filled / Total Value</span>
                <span style={{ fontWeight: 600, color: colors.text.muted, fontVariantNumeric: 'tabular-nums' }}>
                  {fmtVal(fillStats.buyFilledValue)} / {fmtVal(fillStats.buyTotalValue)}
                </span>
              </div>
            </div>
            {/* SELL side */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: colors.accent.red, letterSpacing: '0.06em' }}>SELL SIDE</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: colors.text.secondary }}>
                <span>Filled / Total Qty</span>
                <span style={{ fontWeight: 700, color: colors.text.primary, fontVariantNumeric: 'tabular-nums' }}>
                  {fillStats.sellFilledQty.toLocaleString()} / {fillStats.sellTotalQty.toLocaleString()}
                  <span style={{ color: colors.text.muted, fontWeight: 400 }}>{' '}({fillStats.sellTotalQty > 0 ? ((fillStats.sellFilledQty / fillStats.sellTotalQty) * 100).toFixed(0) : 0}%)</span>
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: colors.text.secondary }}>
                <span>Avg Fill Price</span>
                <span style={{ fontWeight: 700, color: colors.accent.red, fontVariantNumeric: 'tabular-nums' }}>
                  {fillStats.sellAvgFill > 0 ? fillStats.sellAvgFill.toFixed(2) : '—'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: colors.text.secondary }}>
                <span>Filled / Total Value</span>
                <span style={{ fontWeight: 600, color: colors.text.muted, fontVariantNumeric: 'tabular-nums' }}>
                  {fmtVal(fillStats.sellFilledValue)} / {fmtVal(fillStats.sellTotalValue)}
                </span>
              </div>
            </div>
          </div>
        </Panel>
      )}

      {/* Zone 2 — Order Table */}
      <div style={card}>
        {/* Date filter row */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '8px 16px', borderBottom: `1px solid ${colors.border.default}`,
        }}>
          <span style={{ fontSize: 10, color: colors.text.muted, fontWeight: 700, letterSpacing: '0.06em' }}>DATE</span>
          <input
            type="date"
            value={dateFilter}
            onChange={e => setDateFilter(e.target.value)}
            style={{
              background: colors.bg.primary, border: `1px solid ${dateFilter ? colors.accent.blue : colors.border.default}`,
              color: colors.text.primary, borderRadius: 4, padding: '3px 8px', fontSize: 11,
            }}
          />
          {dateFilter && (
            <button
              onClick={() => setDateFilter('')}
              style={{
                padding: '2px 9px', background: 'transparent',
                border: `1px solid ${colors.border.default}`, borderRadius: 4,
                color: colors.text.muted, fontSize: 10, fontWeight: 700, cursor: 'pointer',
              }}
            >
              Clear
            </button>
          )}
          <span style={{ fontSize: 10, color: colors.border.default, marginLeft: 'auto', fontStyle: 'italic' }}>
            Showing orders from current session only
          </span>
        </div>

        {/* Controls row */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 16px', borderBottom: `1px solid ${colors.border.default}`,
        }}>
          {/* Tab strip */}
          <div style={{ display: 'flex', gap: 2 }}>
            {(['all', 'active', 'filled', 'rejected', 'cancelled'] as TabKey[]).map(tab => {
              const count = tab === 'all' ? dateFilteredOrders.length
                : tab === 'active' ? activeCount
                : tab === 'filled' ? filledCount
                : tab === 'cancelled' ? cancelledCount
                : rejectedCount;
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: '4px 11px', border: 'none', borderRadius: 4, cursor: 'pointer',
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                    background: activeTab === tab ? colors.border.default : 'transparent',
                    color: activeTab === tab ? colors.text.primary : colors.text.muted,
                  }}
                >
                  {tab.toUpperCase()}
                  {count > 0 && (
                    <span style={{ fontSize: 9, marginLeft: 4, color: activeTab === tab ? colors.text.secondary : colors.text.muted }}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {/* Symbol filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, color: colors.text.muted }}>Symbol</span>
            <select
              value={filterSymbol}
              onChange={e => setFilterSymbol(e.target.value)}
              style={{
                background: colors.bg.primary, color: colors.text.primary, border: `1px solid ${colors.border.default}`,
                borderRadius: 4, padding: '4px 8px', fontSize: 11, cursor: 'pointer',
              }}
            >
              <option value="all">All Symbols</option>
              {(INSTRUMENTS as { secId: string; label: string }[]).map(inst => (
                <option key={inst.secId} value={inst.secId.toUpperCase()}>{inst.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Table header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(66px,0.8fr) minmax(70px,0.9fr) minmax(52px,0.6fr) minmax(96px,1.3fr) minmax(104px,1.2fr) minmax(76px,0.9fr) minmax(72px,0.85fr) minmax(50px,0.6fr) 28px',
          gap: 8, padding: '7px 16px',
          fontSize: 10, fontWeight: 700, color: colors.text.muted, letterSpacing: '0.06em',
          borderBottom: `1px solid ${colors.border.default}`,
        }}>
          <div>TIME</div>
          <div>SYMBOL</div>
          <div>SIDE</div>
          <div>QTY @ PRICE</div>
          <div>FILL</div>
          <div style={{ textAlign: 'right' }}>AVG PX</div>
          <div>STATUS</div>
          <div>TIF</div>
          <div />
        </div>

        {/* Order rows */}
        <div style={{ maxHeight: 480, overflowY: 'auto' }}>
          {filteredOrders.length === 0 ? (
            <div style={{ padding: '24px 16px', textAlign: 'center', color: colors.text.muted, fontSize: 13 }}>
              {orders.length === 0 ? 'No orders yet.' : 'No orders match the current filters.'}
            </div>
          ) : (
            filteredOrders.map(o => (
              <OrderRow
                key={o.id}
                order={o}
                isExpanded={expandedOrders.has(o.id)}
                onToggle={() => toggleExpand(o.id)}
                onCancel={() => cancelOrder(o.id, o.symbol)}
                onUpdateTif={(tif, expDate, expTime) => updateOrderTif(o.id, tif, expDate, expTime)}
              />
            ))
          )}
        </div>
      </div>

      {/* Zone 3 — Trade Blotter */}
      <Panel
        title="Recent fills"
        badge={recentOwnFills.length > 0 ? recentOwnFills.length : undefined}
        flush
      >
        {recentOwnFills.length === 0 ? (
          <div style={{ padding: '16px', color: colors.text.muted, fontSize: 12 }}>
            No recent fills this session
          </div>
        ) : (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(66px,0.85fr) minmax(52px,0.6fr) minmax(70px,0.9fr) minmax(70px,0.9fr) minmax(90px,1.15fr) minmax(72px,0.9fr) minmax(80px,1fr) minmax(80px,1fr)',
              gap: 8, padding: '7px 16px',
              fontSize: 10, fontWeight: 700, color: colors.text.muted, letterSpacing: '0.06em',
              borderBottom: `1px solid ${colors.border.default}`,
            }}>
              <div>TIME</div>
              <div>SIDE</div>
              <div>SYMBOL</div>
              <div style={{ textAlign: 'right' }}>QTY</div>
              <div style={{ textAlign: 'right' }}>FILL PRICE</div>
              <div>MKR/TKR</div>
              <div style={{ textAlign: 'right' }}>P&L</div>
              <div style={{ textAlign: 'right' }}>SLIP vs MP</div>
            </div>
            <div style={{ maxHeight: 220, overflowY: 'auto' }}>
              {recentOwnFills.map((t, i) => {
                const sideColor = t.side === 'buy' ? colors.accent.green : colors.accent.red;
                const makerDefined = t.is_maker !== undefined && t.is_maker !== null;
                const makerLabel = !makerDefined ? '—' : t.is_maker ? 'MAKER' : 'TAKER';
                const makerBg = !makerDefined ? 'transparent' : t.is_maker ? '#36c98e20' : '#ff8c3a20';
                const makerFg = !makerDefined ? colors.text.muted : t.is_maker ? colors.accent.green : colors.accent.yellow;
                const pnl = t.realized_pnl ?? null;
                const slipBps = Number.isFinite(t.microprice_at_fill) && t.microprice_at_fill! > 0
                  ? ((t.price - t.microprice_at_fill!) / t.microprice_at_fill!) * 10000
                  : null;
                const symLabel = t.symbol
                  ? (INSTRUMENTS as { secId: string; label: string }[]).find(
                      inst => inst.secId.toUpperCase() === t.symbol!.toUpperCase()
                    )?.label ?? t.symbol
                  : selectedSymbol;

                return (
                  <div key={i} style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(66px,0.85fr) minmax(52px,0.6fr) minmax(70px,0.9fr) minmax(70px,0.9fr) minmax(90px,1.15fr) minmax(72px,0.9fr) minmax(80px,1fr) minmax(80px,1fr)',
                    gap: 8, padding: '8px 16px',
                    borderBottom: '1px solid #0a0d10', alignItems: 'center',
                    backgroundColor: t.is_maker ? '#36c98e06' : 'transparent',
                  }}>
                    <div style={{ fontSize: 11, color: colors.text.muted }}>{timeShort(t.time)}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: sideColor }}>
                      {t.side.toUpperCase()}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: colors.text.primary }}>{symLabel}</div>
                    <div style={{ textAlign: 'right', fontSize: 11, color: colors.text.primary }}>
                      {t.size.toLocaleString()}
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 12, fontWeight: 600, color: colors.text.primary }}>
                      {t.price.toFixed(2)}
                    </div>
                    <div>
                      <span style={{
                        display: 'inline-block', padding: '1px 7px', borderRadius: 3,
                        background: makerBg, fontSize: 9, fontWeight: 700, color: makerFg,
                      }}>
                        {makerLabel}
                      </span>
                    </div>
                    <div style={{
                      textAlign: 'right', fontSize: 12, fontWeight: 600,
                      color: pnl === null ? colors.text.muted : pnl >= 0 ? colors.accent.green : colors.accent.red,
                    }}>
                      {pnl === null ? '—' : `${pnl >= 0 ? '+' : ''}${pnl.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                    </div>
                    <div style={{
                      textAlign: 'right', fontSize: 11, fontWeight: 600,
                      color: slipBps === null ? colors.text.muted : slippageColor(t.side, slipBps),
                    }}>
                      {slipBps === null ? '—' : `${slipBps >= 0 ? '+' : ''}${slipBps.toFixed(1)} bp`}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Panel>
    </div>
  );
}

function StatChip({ label, count, color }: { label: string; count: number; color?: string }) {
  return (
    <div style={{
      flex: 1, background: colors.bg.secondary, borderRadius: 8, border: `1px solid ${colors.border.default}`,
      padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 4,
    }}>
      <div style={{ fontSize: 10, color: colors.text.muted, fontWeight: 700, letterSpacing: '0.06em' }}>
        {label.toUpperCase()}
      </div>
      <div style={{ fontSize: 20, fontWeight: 800, color: color ?? colors.text.primary }}>{count}</div>
    </div>
  );
}

interface OrderRowProps {
  order: Order;
  isExpanded: boolean;
  onToggle: () => void;
  onCancel: () => void;
  onUpdateTif: (tif: number, expireDate?: string, expireTime?: string) => void;
}

function parseOrderExpDate(d?: string) {
  return d ? `${d.slice(0,4)}-${d.slice(4,6)}-${d.slice(6,8)}` : '';
}

function OrderRow({ order, isExpanded, onToggle, onCancel, onUpdateTif }: OrderRowProps) {
  const [editTif, setEditTif] = useState<number>(order.timeInForce ?? 1);
  const [editExpDate, setEditExpDate] = useState<string>(parseOrderExpDate(order.expireDate));
  const [editExpTime, setEditExpTime] = useState<string>(order.expireTime?.slice(0,5) ?? '');
  const [tifSaved, setTifSaved] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const prevExpandedRef = React.useRef(isExpanded);

  // Re-sync edit state from order prop each time the row is opened,
  // so saved values are reflected immediately on reopen.
  React.useEffect(() => {
    if (isExpanded && !prevExpandedRef.current) {
      setEditTif(order.timeInForce ?? 1);
      setEditExpDate(parseOrderExpDate(order.expireDate));
      setEditExpTime(order.expireTime?.slice(0,5) ?? '');
    }
    prevExpandedRef.current = isExpanded;
  }, [isExpanded, order.timeInForce, order.expireDate, order.expireTime]);

  function saveTif() {
    const expDate = editTif === 6 && editExpDate ? editExpDate.replace(/-/g, '') : undefined;
    const expTime = editTif === 6 && editExpTime ? `${editExpTime}:00` : undefined;
    onUpdateTif(editTif, expDate, expTime);
    setTifSaved(true);
    setTimeout(() => setTifSaved(false), 2000);
  }
  const isCancellable = order.status === 'new' || order.status === 'partially_filled';
  const filledQty = order.cumQty ?? 0;
  const fillPct = order.qty > 0 ? (filledQty / order.qty) * 100 : 0;
  const sideColor = order.side === 'buy' ? colors.accent.green : colors.accent.red;
  const statusColor = STATUS_COLORS[order.status] ?? colors.text.secondary;

  const symLabel = (INSTRUMENTS as { secId: string; label: string }[]).find(
    i => i.secId.toUpperCase() === symbolToSecId(order.symbol)
  )?.label ?? order.symbol;

  const progressStatus = order.status;
  // Only a live order can have its TIF amended; a terminal one shows a read-only value.
  const isOpen = order.status === 'new' || order.status === 'partially_filled';

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(66px,0.8fr) minmax(70px,0.9fr) minmax(52px,0.6fr) minmax(96px,1.3fr) minmax(104px,1.2fr) minmax(76px,0.9fr) minmax(72px,0.85fr) minmax(50px,0.6fr) 28px',
          gap: 8, padding: '10px 16px',
          borderLeft: `3px solid ${sideColor}`,
          cursor: 'pointer', alignItems: 'center',
          transition: 'background-color 0.15s',
          // While open the row stays lit, so the panel below is unambiguously ITS panel.
          backgroundColor: isExpanded ? `${sideColor}14` : 'transparent',
          borderBottom: isExpanded ? 'none' : `1px solid ${colors.border.default}`,
        }}
        onClick={onToggle}
        onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.backgroundColor = colors.bg.tertiary; }}
        onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        <div style={{ fontSize: 11, color: colors.text.muted }}>{timeShort(order.time)}</div>
        <div style={{ fontSize: 12, fontWeight: 700, color: colors.text.primary }}>{symLabel}</div>
        <div style={{ fontSize: 11, fontWeight: 700, color: sideColor }}>
          {order.side.toUpperCase()}
        </div>
        <div style={{ fontSize: 11, color: colors.text.primary }}>
          <span style={{ fontWeight: 700 }}>{order.qty.toLocaleString()}</span>
          <span style={{ color: colors.text.muted }}> @ </span>
          <span>{order.price > 0
            ? order.price.toLocaleString(undefined, { minimumFractionDigits: 2 })
            : '—'}
          </span>
        </div>
        {/* Inline fill progress */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{
            // Track must sit ABOVE the row ground or an unfilled bar is invisible; bg.primary
            // is the page colour and read as nothing at all.
            flex: 1, height: 5, backgroundColor: colors.bg.tertiary, border: `1px solid ${colors.border.default}`,
            borderRadius: 2.5, overflow: 'hidden', boxSizing: 'border-box',
          }}>
            <div style={{
              height: '100%', width: `${fillPct}%`,
              backgroundColor: statusColor, borderRadius: 2.5, transition: 'width 0.4s',
            }} />
          </div>
          <span style={{ fontSize: 9, color: colors.text.muted, minWidth: 26, textAlign: 'right' }}>
            {fillPct.toFixed(0)}%
          </span>
        </div>
        {/* Avg fill price */}
        <div style={{
          textAlign: 'right', fontSize: 11, fontWeight: 600,
          color: order.avgFillPrice ? colors.text.primary : colors.text.muted,
        }}>
          {order.avgFillPrice ? order.avgFillPrice.toFixed(2) : '—'}
        </div>
        {/* Status badge */}
        <div>
          <span style={{
            display: 'inline-block', padding: '2px 7px', borderRadius: 4,
            backgroundColor: `${statusColor}22`, color: statusColor,
            fontSize: 9, fontWeight: 700, letterSpacing: '0.05em',
          }}>
            {order.status === 'partially_filled' ? 'PARTIAL' : order.status.toUpperCase()}
          </span>
        </div>
        {/* TIF badge — own column */}
        <div>
          {order.timeInForce != null ? (
            <span style={{
              display: 'inline-block', padding: '2px 6px', borderRadius: 4,
              backgroundColor: `${colors.accent.blue}18`, color: colors.accent.blue,
              fontSize: 9, fontWeight: 700, letterSpacing: '0.05em',
            }}>
              {TIF_LABELS[order.timeInForce] ?? `TIF${order.timeInForce}`}
            </span>
          ) : (
            <span style={{ fontSize: 9, color: colors.text.muted }}>—</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
          {isCancellable && !confirmingCancel && (
            <button
              onClick={(e) => { e.stopPropagation(); setConfirmingCancel(true); }}
              title="Cancel order"
              style={{
                background: 'none', border: '1px solid #ff5c5c44', borderRadius: 4,
                padding: '2px 4px', cursor: 'pointer', display: 'flex', alignItems: 'center',
              }}
            >
              <X size={12} color="#ff5c5c" />
            </button>
          )}
          {confirmingCancel && (
            <div
              onClick={e => e.stopPropagation()}
              style={{ display: 'flex', alignItems: 'center', gap: 3 }}
            >
              <button
                onClick={(e) => { e.stopPropagation(); onCancel(); setConfirmingCancel(false); }}
                style={{
                  padding: '2px 6px', fontSize: 9, fontWeight: 700, borderRadius: 3,
                  background: '#ff5c5c', color: 'white', border: 'none', cursor: 'pointer',
                  letterSpacing: '0.04em',
                }}
              >
                CANCEL
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setConfirmingCancel(false); }}
                style={{
                  padding: '2px 5px', fontSize: 9, fontWeight: 700, borderRadius: 3,
                  background: '#2a3540', color: '#7e8a96', border: 'none', cursor: 'pointer',
                }}
              >
                NO
              </button>
            </div>
          )}
          {!confirmingCancel && (isExpanded
            ? <ChevronUp size={14} color="#7a8693" />
            : <ChevronDown size={14} color="#7a8693" />)}
        </div>
      </div>

      {/* Expanded panel */}
      {isExpanded && (
        <div style={{
          // Inset from the left and carrying a thicker accent in the row's own side colour, so
          // the panel reads as hanging off the row above rather than sitting between two rows.
          padding: '16px 16px 16px 22px',
          marginLeft: 18,
          backgroundColor: colors.bg.inset,
          borderLeft: `4px solid ${sideColor}`,
          borderBottom: `1px solid ${colors.border.default}`,
        }}>
          {/* Fill progress bar */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: colors.text.muted, fontWeight: 600 }}>Fill Progress</span>
              <span style={{ fontSize: 11, color: colors.text.muted }}>
                {filledQty.toLocaleString()} / {order.qty.toLocaleString()} ({fillPct.toFixed(1)}%)
              </span>
            </div>
            <ProgressBar filled={filledQty} total={order.qty} status={progressStatus} />
          </div>

          {/* detail row */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr',
            gap: 12, marginBottom: 14, fontSize: 12,
          }}>
            <div>
              <div style={{ color: colors.text.muted, marginBottom: 3 }}>Limit Price</div>
              <div style={{ color: colors.text.primary, fontWeight: 600 }}>
                {order.price > 0 ? order.price.toFixed(2) : '—'}
              </div>
            </div>
            <div>
              <div style={{ color: colors.text.muted, marginBottom: 3 }}>Avg Fill Price</div>
              <div style={{ color: colors.text.primary, fontWeight: 600 }}>
                {order.avgFillPrice ? order.avgFillPrice.toFixed(2) : '—'}
              </div>
            </div>
            <div>
              <div style={{ color: colors.text.muted, marginBottom: 3 }}>Time In Force</div>
              {isOpen ? (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <select
                    value={editTif}
                    onChange={e => setEditTif(Number(e.target.value))}
                    style={{
                      width: 74, flexShrink: 0, padding: '4px 6px', fontSize: 11,
                      background: colors.bg.primary, color: colors.text.primary,
                      border: `1px solid ${colors.border.default}`, borderRadius: 4,
                    }}
                  >
                    {TIF_OPTIONS.map(({ v, label, desc }) => (
                      <option key={v} value={v} title={desc}>{label}</option>
                    ))}
                  </select>
                  <button
                    onClick={saveTif}
                    style={{
                      padding: '4px 10px', fontSize: 10, fontWeight: 700, borderRadius: 4,
                      cursor: 'pointer', border: 'none', flexShrink: 0,
                      backgroundColor: tifSaved ? colors.accent.green : colors.accent.blue,
                      color: 'white',
                    }}
                  >
                    {tifSaved ? 'Saved' : 'Apply'}
                  </button>
                </div>
              ) : (
                <div style={{ color: colors.accent.blue, fontWeight: 600 }}>
                  {order.timeInForce != null ? (TIF_LABELS[order.timeInForce] ?? `TIF${order.timeInForce}`) : 'Day'}
                </div>
              )}
              {/* GTD needs an expiry, so those inputs appear only for that selection. */}
              {isOpen && editTif === 6 && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 6 }}>
                  <input
                    type="date"
                    value={editExpDate}
                    onChange={e => setEditExpDate(e.target.value)}
                    style={{ width: '100%', padding: '4px 6px', background: colors.bg.primary, color: colors.text.primary, border: `1px solid ${colors.border.default}`, borderRadius: 4, fontSize: 10, boxSizing: 'border-box' }}
                  />
                  <input
                    type="time"
                    value={editExpTime}
                    onChange={e => setEditExpTime(e.target.value)}
                    style={{ width: '100%', padding: '4px 6px', background: colors.bg.primary, color: colors.text.primary, border: `1px solid ${colors.border.default}`, borderRadius: 4, fontSize: 10, boxSizing: 'border-box' }}
                  />
                </div>
              )}
              {!isOpen && (order.expireDate || order.expireTime) && (
                <div style={{ color: colors.text.muted, fontSize: 10, marginTop: 2 }}>
                  {order.expireDate ? `exp ${order.expireDate}` : `@ ${order.expireTime}`}
                </div>
              )}
            </div>
            {order.exchangeOrderId && (
              <div>
                <div style={{ color: colors.text.muted, marginBottom: 3 }}>Exchange Order ID</div>
                <div style={{ color: colors.text.secondary, fontSize: 10, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {order.exchangeOrderId}
                </div>
              </div>
            )}
          </div>

          {/* Fill history */}
          {order.fills && order.fills.length > 0
            ? <FillHistoryTable fills={order.fills} order={order} />
            : (
              <div style={{
                padding: '12px 16px', textAlign: 'center',
                color: colors.text.muted, fontSize: 12,
                background: colors.bg.secondary, borderRadius: 6, border: `1px solid ${colors.border.default}`,
              }}>
                No fills recorded yet
              </div>
            )}
        </div>
      )}
    </>
  );
}
